import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, LayoutDashboard, Package, Users, FileText,
  ClipboardList, ShoppingCart, Tag, DollarSign,
  FileBadge, ScrollText, CheckSquare, Settings, X
} from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';

const COMMANDS = [
  { group: 'Navigation', items: [
    { id: 'dash',       label: 'Go to Dashboard',         icon: LayoutDashboard, path: '/dashboard' },
    { id: 'req',        label: 'Go to Requests',           icon: ClipboardList,   path: '/requests' },
    { id: 'orders',     label: 'Go to Orders',             icon: ShoppingCart,    path: '/orders' },
    { id: 'suppliers',  label: 'Go to Suppliers',          icon: Users,           path: '/data/suppliers' },
    { id: 'items',      label: 'Go to Items',              icon: Package,         path: '/data/items' },
    { id: 'contracts',  label: 'Go to Contracts',          icon: FileBadge,       path: '/data/contracts' },
    { id: 'cats',       label: 'Go to Category Codes',     icon: Tag,             path: '/data/categories' },
    { id: 'currencies', label: 'Go to Currency Codes',     icon: DollarSign,      path: '/data/currencies' },
    { id: 'tnc',        label: 'Go to Terms & Conditions', icon: ScrollText,      path: '/data/terms' },
    { id: 'approvals',  label: 'Go to Approvals',          icon: CheckSquare,     path: '/approvals' },
    { id: 'ac',         label: 'Go to Account Codes',      icon: FileText,        path: '/account-codes' },
  ]},
  { group: 'Admin', items: [
    { id: 'users',      label: 'Manage Users',             icon: Users,           path: '/admin/users' },
    { id: 'settings',   label: 'Environment Settings',     icon: Settings,        path: '/admin/settings' },
    { id: 'version',    label: 'Version History',          icon: FileText,        path: '/admin/version-history' },
  ]},
];

const ALL_ITEMS = COMMANDS.flatMap(g => g.items.map(i => ({ ...i, group: g.group })));

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  const filtered = query
    ? ALL_ITEMS.filter(i => i.label.toLowerCase().includes(query.toLowerCase()))
    : ALL_ITEMS;

  useEffect(() => { if (open) { setQuery(''); setActiveIdx(0); } }, [open]);

  const select = (item) => {
    navigate(item.path);
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && filtered[activeIdx]) select(filtered[activeIdx]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="p-0 overflow-hidden max-w-lg gap-0" aria-label="Command palette">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={handleKey}
            placeholder="Search pages and actions..."
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            data-testid="command-palette-input"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-results"
            aria-activedescendant={filtered[activeIdx] ? `cmd-item-${filtered[activeIdx].id}` : undefined}
            aria-label="Search pages and actions"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground" aria-label="Clear search">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">Esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto py-2" id="command-results" role="listbox" aria-label="Search results">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">No results for &ldquo;{query}&rdquo;</p>
          ) : (
            query ? (
              <ul>
                {filtered.map((item, i) => (
                  <CommandItem key={item.id} item={item} isActive={i === activeIdx}
                    onSelect={() => select(item)} onHover={() => setActiveIdx(i)} />
                ))}
              </ul>
            ) : (
              COMMANDS.map(group => {
                const groupItems = group.items;
                return (
                  <div key={group.group}>
                    <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      {group.group}
                    </p>
                    <ul>
                      {groupItems.map((item, i) => {
                        const globalIdx = ALL_ITEMS.findIndex(x => x.id === item.id);
                        return (
                          <CommandItem key={item.id} item={item} isActive={globalIdx === activeIdx}
                            onSelect={() => select(item)} onHover={() => setActiveIdx(globalIdx)} />
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )
          )}
        </div>

        <div className="px-4 py-2 border-t flex items-center gap-4 text-[10px] text-muted-foreground">
          <span><kbd className="rounded border bg-muted px-1 font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="rounded border bg-muted px-1 font-mono">↵</kbd> open</span>
          <span><kbd className="rounded border bg-muted px-1 font-mono">Esc</kbd> close</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CommandItem({ item, isActive, onSelect, onHover }) {
  return (
    <li role="option" id={`cmd-item-${item.id}`} aria-selected={isActive}>
      <button
        className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors ${
          isActive ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent/50'
        }`}
        onClick={onSelect}
        onMouseEnter={onHover}
        tabIndex={-1}
      >
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isActive ? 'bg-primary/15' : 'bg-muted'
        }`}>
          <item.icon className={`h-3.5 w-3.5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
        </div>
        <span className="flex-1 truncate">{item.label}</span>
        <span className="text-[10px] text-muted-foreground">{item.group}</span>
      </button>
    </li>
  );
}
